import React from 'react';

/**
 * Cycle of Addiction — Week 3 Self-Work diagram.
 *
 * SVG rendered per ARP_Emergent_W03_SelfWork_Prompt_V1 §4 + §3.  Static,
 * monochrome (Georgia serif elsewhere on the page; SVG uses the system serif
 * via the inline font-family attribute).  Nodes are NOT tappable; this is a
 * reference visual only.  Responsive: scales with parent width while
 * preserving 1:1 aspect ratio.
 *
 * Shading convention is intentional per §3:
 *   • Stage 1 + centre  = solid black  (#1a1a1a) — pre-use stable
 *   • Stages 2,3,4,7,8  = dark grey    (#333333) — pre-use psychological states
 *   • Stages 5,6        = medium grey  (#555555) — acute use period
 * Do NOT alter without content-authority sign-off.
 */

const STAGES = [
  { n: 1, label: 'Content & Stable',                fill: '#1a1a1a', textFill: '#ffffff' },
  { n: 2, label: 'Stressor',                        fill: '#333333', textFill: '#ffffff' },
  { n: 3, label: 'Restless,\u00a0Irritable,\nDiscontented', fill: '#333333', textFill: '#ffffff' },
  { n: 4, label: 'Psychic Change',                  fill: '#333333', textFill: '#ffffff' },
  { n: 5, label: 'First Use',                       fill: '#555555', textFill: '#ffffff' },
  { n: 6, label: 'Drinking /\nDrug Spree',          fill: '#555555', textFill: '#ffffff' },
  { n: 7, label: 'Emerging\nRemorseful',            fill: '#333333', textFill: '#ffffff' },
  { n: 8, label: 'Obsession\nReturns',              fill: '#333333', textFill: '#ffffff' },
];

const VB = 500;             // viewBox size
const CENTER = VB / 2;      // 250
const CIRCLE_R = 180;       // node-centre radius
const NODE_R = 52;          // node radius

function nodeCenter(i) {
  // Start at top (12 o'clock), go clockwise.  i = 0..7.
  const angle = -Math.PI / 2 + (i * 2 * Math.PI) / STAGES.length;
  return {
    x: CENTER + CIRCLE_R * Math.cos(angle),
    y: CENTER + CIRCLE_R * Math.sin(angle),
  };
}

// Build the curved arrow path between node i and node i+1, hugging the outer
// arc rather than cutting across the centre.
function arcArrow(i) {
  const next = (i + 1) % STAGES.length;
  const start = nodeCenter(i);
  const end = nodeCenter(next);
  // Tangent offset away from node so arrow doesn't visually clip the circle.
  const startAngle = -Math.PI / 2 + (i * 2 * Math.PI) / STAGES.length;
  const endAngle = -Math.PI / 2 + (next * 2 * Math.PI) / STAGES.length;
  const startOffset = startAngle + Math.PI / 2 - 0.18;
  const endOffset = endAngle - Math.PI / 2 + 0.18;
  const x1 = start.x + NODE_R * Math.cos(startOffset);
  const y1 = start.y + NODE_R * Math.sin(startOffset);
  const x2 = end.x + NODE_R * Math.cos(endOffset + Math.PI);
  const y2 = end.y + NODE_R * Math.sin(endOffset + Math.PI);
  // Arc sweeps along the outer track; large-arc=0, sweep=1 (clockwise)
  return `M ${x1.toFixed(2)} ${y1.toFixed(2)} A ${CIRCLE_R + 8} ${CIRCLE_R + 8} 0 0 1 ${x2.toFixed(2)} ${y2.toFixed(2)}`;
}

export default function CycleOfAddictionDiagramW3() {
  return (
    <div className="w-full max-w-[520px] mx-auto" data-testid="cycle-of-addiction-w3-diagram">
      <svg
        viewBox={`0 0 ${VB} ${VB}`}
        xmlns="http://www.w3.org/2000/svg"
        role="img"
        aria-label="Cycle of Addiction diagram showing eight stages in a circular sequence. Stage nodes are not interactive."
        style={{ width: '100%', height: 'auto', fontFamily: 'Georgia, serif' }}
      >
        <defs>
          <marker
            id="w3-arrowhead"
            viewBox="0 0 10 10"
            refX="8"
            refY="5"
            markerWidth="6"
            markerHeight="6"
            orient="auto-start-reverse"
            markerUnits="strokeWidth"
          >
            <path d="M 0 0 L 10 5 L 0 10 z" fill="#1a1a1a" />
          </marker>
        </defs>

        {/* Dashed outer track */}
        <circle
          cx={CENTER}
          cy={CENTER}
          r={CIRCLE_R + 8}
          fill="none"
          stroke="#dddddd"
          strokeWidth="1.4"
          strokeDasharray="6 6"
        />

        {/* Arrows (drawn before nodes so nodes sit on top) */}
        {STAGES.map((s, i) => (
          <path
            key={`arrow-${i}`}
            d={arcArrow(i)}
            fill="none"
            stroke="#1a1a1a"
            strokeWidth="1.6"
            markerEnd="url(#w3-arrowhead)"
          />
        ))}

        {/* Centre label */}
        <circle cx={CENTER} cy={CENTER} r={64} fill="#1a1a1a" />
        <text
          x={CENTER}
          y={CENTER - 6}
          textAnchor="middle"
          fontSize="14"
          fontWeight="bold"
          fill="#ffffff"
          letterSpacing="0.5"
        >
          CYCLE OF
        </text>
        <text
          x={CENTER}
          y={CENTER + 14}
          textAnchor="middle"
          fontSize="14"
          fontWeight="bold"
          fill="#ffffff"
          letterSpacing="0.5"
        >
          ADDICTION
        </text>

        {/* Nodes */}
        {STAGES.map((s, i) => {
          const { x, y } = nodeCenter(i);
          const lines = s.label.split('\n');
          return (
            <g key={`node-${s.n}`}>
              <circle cx={x} cy={y} r={NODE_R} fill={s.fill} stroke="#000" strokeWidth="0.6" />
              {/* Stage number badge: small white pill at top of node */}
              <circle cx={x} cy={y - NODE_R + 12} r={10} fill="#ffffff" stroke="#000" strokeWidth="0.8" />
              <text
                x={x}
                y={y - NODE_R + 16}
                textAnchor="middle"
                fontSize="11"
                fontWeight="bold"
                fill="#1a1a1a"
              >
                {s.n}
              </text>
              {/* Stage label, possibly multi-line */}
              {lines.map((line, li) => (
                <text
                  key={li}
                  x={x}
                  y={y + 6 + (li - (lines.length - 1) / 2) * 12}
                  textAnchor="middle"
                  fontSize={lines.length > 1 ? '10' : '11'}
                  fontWeight="600"
                  fill={s.textFill}
                >
                  {line}
                </text>
              ))}
            </g>
          );
        })}
      </svg>
    </div>
  );
}
